import { forwardRef, useEffect, useRef, useState } from 'react';
import { Auth, ConfirmationResult, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import toast from 'react-hot-toast';
import LoadingIcon from 'components/loading/LoadingIcon';
import OtpInput from 'components/otp-input/OtpInput';
import styles from './verify-phone.style';

interface Props {
    phone: string;
    onCancel: () => void;
    onSuccess: (firebaseId: string) => void;
    firebaseConfig: Auth;
}

const VerifyPhone = forwardRef(({ phone, onCancel, onSuccess, firebaseConfig }: Props, ref) => {
    const [loading, setLoading] = useState(false);
    const [confirmResult, setConfirmResult] = useState<ConfirmationResult | null>(null);
    const recaptchaRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Format số điện thoại
        let phoneFormat = phone.startsWith('0') ? phone.slice(1) : phone;

        const signInWithPhone = async () => {
            if (!recaptchaRef.current) return;

            try {
                // Tạo RecaptchaVerifier nếu chưa có
                if (!(window as any).recaptchaVerifier) {
                    (window as any).recaptchaVerifier = new RecaptchaVerifier(
                        recaptchaRef.current,
                        { size: 'invisible' },
                        firebaseConfig
                    );
                }

                const response = await signInWithPhoneNumber(
                    firebaseConfig,
                    `+84${phoneFormat}`,
                    (window as any).recaptchaVerifier
                );

                setConfirmResult(response);
            } catch (error: any) {
                console.error(error);
                if (error.code === 'auth/too-many-requests') {
                    toast.error('Thao tác quá nhanh, vui lòng thử lại sau vài phút');
                } else if (error.code === 'auth/invalid-phone-number') {
                    toast.error('Số điện thoại không hợp lệ');
                } else {
                    toast.error('Có lỗi xảy ra! Vui lòng liên hệ bộ phận hỗ trợ');
                }

                // Reset recaptcha
                if (recaptchaRef.current) {
                    recaptchaRef.current.innerHTML = '';
                }
            }
        };

        signInWithPhone();

        return () => {
            // Cleanup recaptcha khi component unmount
            if ((window as any).recaptchaVerifier) {
                (window as any).recaptchaVerifier.clear();
                delete (window as any).recaptchaVerifier;
            }
        };
    }, [phone, firebaseConfig]);

    const handleVerifyOtp = async (otp: string, cb: () => void) => {
        if (!otp || !confirmResult) return;

        setLoading(true);
        try {
            const result = await confirmResult.confirm(otp);
            const user = result.user;
            const token = await user.getIdToken();
            onSuccess(token);
        } catch (error: any) {
            console.error(error);
            if (error.code === 'auth/invalid-verification-code') {
                toast.error('Mã xác nhận không đúng!');
            } else if (error.code === 'auth/code-expired') {
                toast.error('Mã xác nhận đã hết thời gian sử dụng!');
            }
            cb();
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className={`bg-popup-verify ${styles.wrap}`}>
                <h6 className={styles.title}>Xác Minh Số Điện Thoại</h6>
                <h6 className={styles.optTitle}>Nhập mã xác nhận được gửi về thuê bao</h6>
                <p className={styles.phoneValue}>{phone}</p>

                <OtpInput onSubmit={handleVerifyOtp} onCancel={onCancel} />

                {/* Container recaptcha luôn nằm trong component */}
                <div ref={recaptchaRef} />
            </div>

            {loading && <LoadingIcon />}
        </>
    );
});

export default VerifyPhone;
